import { Component, computed, inject, OnInit } from '@angular/core';
import {
  NbAccordionModule,
  NbFormFieldModule,
  NbInputModule,
  NbListModule,
  NbSelectModule,
  NbTagModule,
} from '@nebular/theme';
import { CommonModule, formatNumber } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BudgetForm, IInvestmentWizard } from '../wizard-interface';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { InvestmentWizardService, WizardStep } from '../../../../../@core/utils/investment-wizard-service';
import { KPI } from '../../../../../@core/api';
import { ConfigService, I18nService, InvestmentNotationsService, ScopeService } from '../../../../../@core/utils';
import { RoadmapService } from '../../../../../@core/utils';
import { RightPanelDialogService } from '../../../../../@core/utils/right-panel-dialog.service';
import { InvestmentReasonsService } from '../../../../../@core/utils/investment-reasons.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { idMapToArray } from '../../../../../@core/utils/scope.service';
import { InvestmentWizardQuickEditComponent } from '../wizard-quick-edit.component';

export interface InvestmentSubmitForm {
  label: string;
  // optional
  investment_category: KPI.InvestmentCategory;
}

@Component({
  selector: 'app-investment-submit-selection',
  standalone: true,
  imports: [
    NbListModule,
    CommonModule,
    NbInputModule,
    NbFormFieldModule,
    TranslateModule,
    ReactiveFormsModule,
    NbAccordionModule,
    NbTagModule,
    NbSelectModule,
  ],
  template: `
    <form [formGroup]="form" class="investment-form wrapper" autocomplete="off">
      <div class="form-group">
        <div class="form-field attention">
          <label for="label">
            {{ "Investment label" | translate }}
          </label>
          <input nbInput id="label" formControlName="label" fullWidth placeholder="-" autocomplete="off" />
        </div>
        <!--        Perimeter-->
        <div class="form-field">
          <label for="perimeter">{{ "Perimeter" | translate }}</label>
          <span>{{ getPerimeter() }}</span>
        </div>

        <!--        Investment type-->
        <div class="form-field pointer">
          <label for="investment_type">{{ "Investment type" | translate }} </label>
          <span>{{ investmentType?.name }}</span>
        </div>
        <!--        Budget-->
        <div class="form-field display-with-link" (click)="openQuickEdit('BUDGET_ALLOCATION')">
          <label for="budget">{{ "Budget" | translate }} </label>
          <div>
            <span>{{ getBudget() }}</span>
            <span class="right-chevron">
            <i class="far fa-chevron-right fal"></i>
          </span>
          </div>
        </div>
        <!--        Status -->
        <div class="form-field pointer display-with-link" (click)="openQuickEdit('BUDGET_ALLOCATION')">
          <label for="status">{{ "Status" | translate }} </label>
          <div>
            <span>{{ getInvestmentStatus() }}</span>
            <span class="right-chevron">
            <i class="far fa-chevron-right fal"></i>
            </span>
          </div>
        </div>
        <!--        Priority-->
        <div class="form-field display-with-link">
          <label for="priority">{{ "Priority" | translate }}</label>
          <nb-select fullWidth [selected]="getInvestmentPriority()"
                     (selectedChange)="updateBudgetAllocation('priority', $event)">
            @for (priority of (investmentPriorities$ | async); track priority) {
              <nb-option [value]="priority">{{ priority.name }}</nb-option>
            }
          </nb-select>
        </div>
        <!--        InvestmentNotation-->
        @if ((investmentType?.maintenance || investmentType?.renew) && !!selectedAssetState) {
          <div class="form-field display-with-link" (click)="openQuickEdit('INVESTMENT_NOTATION_STEP')">
            <label for="priority">{{ "Investment Notation" | translate }}</label>
            <div>
              <span>{{ getInvestmentNotation() }}</span>
              <span class="right-chevron">
            <i class="far fa-chevron-right fal"></i>
            </span>
            </div>
          </div>
        }
        <!--        Impact-->
        @if (roadmapIndicators().length > 0) {
          <div class="form-field pointer display-with-link" (click)="openQuickEdit('INVESTMENT_IMPACT_SELECTION')">
            <label for="impact">{{ "Impact" | translate }} </label>
            <div>
              <span>{{ getInvestmentImpact() }}</span>
              <span class="right-chevron">
              <i class="far fa-chevron-right fal"></i>
            </span>
            </div>
          </div>
        }

        <!--        Capex-->
        <div class="form-field">
          <label for="capex">{{ "Capex" | translate }}</label>
          <nb-select fullWidth [selected]="getInvestmentCapex()"
                     (selectedChange)="updateBudgetAllocation('is_capex', $event)">
            <nb-option [value]="true">{{ "Yes" | translate }}</nb-option>
            <nb-option [value]="false">{{ "No" | translate }}</nb-option>
          </nb-select>
        </div>
        <!--        taxonomy-->
        <div class="form-field">
          <label for="taxonomy">{{ "Potentially aligned with EU taxonomy" | translate }}</label>
          <span>{{ getInvestmentTaxonomy() }}</span>
        </div>
        <!--        nomenclature technique-->
        <!--        nomenclature technique-->
        <div class="form-field">
          <label for="taxonomy">{{ "Technical nomenclature" | translate }}</label>
          <span>{{ getTechnicalNomenclature() }}</span>
        </div>
        <!--        investment_reasons-->
        @if (platformReasons().length > 0) {
          <div class="form-field display-with-link pointer" (click)="openQuickEdit('INVESTMENT_REASON_SELECTION')">
            <label for="investment_reasons">{{ "Tag" | translate }}</label>
            <div>
              @if (!getInvestmentReasons()?.length) {
                <span>-</span>
              } @else {
                <nb-tag-list class="my-1">
                  @for (reason of getInvestmentReasons(); track reason.id) {
                    <nb-tag [text]="reason.name"
                            [ngStyle]="{'background-color': reason.background_color, 'font-size': '12px'}"></nb-tag>
                  }
                </nb-tag-list>
                <span class="right-chevron">
              <i class="far fa-chevron-right fal"></i>
            </span>
              }
            </div>
          </div>
        }

        <!--        further information-->
        @if ((furtherInformations$ | async)?.length > 0) {
          <div class="form-field pointer display-with-link" (click)="openQuickEdit('INVESTMENT_FURTHER_INFORMATION_SELECTION')">
            <label for="further_information">{{ "Further information" | translate }}</label>
            <div>
              <span>{{ getFurtherInformation() }}</span>
              <span class="right-chevron">
                <i class="far fa-chevron-right fal"></i>
              </span>
            </div>
          </div>
        }
        <nb-accordion>
          <nb-accordion-item expanded="false" disabled="true" class="accordion-item-as-button">
            <nb-accordion-item-header>
              {{ "Optional" | translate }}
            </nb-accordion-item-header>
          </nb-accordion-item>
        </nb-accordion>
        <!--        gain energy carbon-->
        @if (energyEnabled) {
          <div class="form-field display-with-link" (click)="openQuickEdit('GAIN_ENERGY_CARBON')">
            <label for="energy_carbon">{{ "Gain energy / carbone" | translate }}</label>
            <div>
              <span>{{ getGainEnergyCarbon() }}</span>
              <span class="right-chevron">
              <i class="far fa-chevron-right fal"></i>
            </span>
            </div>
          </div>
        }

        <div class="form-field display-with-link" (click)="openQuickEdit('INVESTMENT_CATEGORY_SELECTION')">
          <label for="investment_category">{{ "investment.investment_category" | translate }}</label>
          <div>
            <span>{{ getInvestmentCategory() }}</span>
            <span class="right-chevron">
              <i class="far fa-chevron-right fal"></i>
            </span>
          </div>
        </div>

        <div class="form-field">
          <label>{{ "Linked equipment" | translate }}</label>
          <div>
            <span>{{ getLinkedEquipmentDisplay() }}</span>
          </div>
        </div>
      </div>
    </form>
  `,
  styleUrls: ['../wizard-index.component.scss', '../../../side-forms/base-form/base-form.component.scss'],
})
export class InvestmentSubmitComponent implements OnInit, IInvestmentWizard {
  readonly stateKey = 'INVESTMENT_SUBMIT_STEP';
  state = () => this.wizardService.getState(this.stateKey);

  // dependencies
  private fb = inject(FormBuilder);
  private i18n = inject(I18nService);
  private translate = inject(TranslateService);
  private scope = inject(ScopeService);
  private rightPanelDialogService = inject(RightPanelDialogService);
  private wizardService = inject(InvestmentWizardService);
  private ivNotationService = inject(InvestmentNotationsService);
  private config = inject(ConfigService);
  private roadmapService = inject(RoadmapService);
  private investmentReasonsService = inject(InvestmentReasonsService);

  // internal state
  private assetTypes = toSignal(this.scope.assetTypes$.pipe(
    idMapToArray(),
  ), { initialValue: [] });

  form = this.fb.group({
    label: ['', Validators.required],
  });

  get investmentType(): KPI.InvestmentType { return this.wizardService.getState('INVESTMENT_TYPE_SELECTION'); }
  get selectedAssetState() { return this.wizardService.getState('ASSET_SELECTION'); }
  investmentPriorities$ = this.scope.investmentPriorities$.pipe(
    takeUntilDestroyed(),
    idMapToArray(),
  );
  energyEnabled = this.config.getSettingValue('ENERGY__ENABLE', false);
  roadmapIndicators = toSignal(this.roadmapService.roadmapIndicators(), { initialValue: [] });
  platformReasons = toSignal(this.investmentReasonsService.investmentReasons$, { initialValue: [] });

  get budgetAllocation() { return this.wizardService.getState('BUDGET_ALLOCATION'); }

  furtherInformations$ = this.scope.getInvestFurtherInfo$.pipe(idMapToArray());

  getInvestmentImpact = () => {
    const state = this.wizardService.getState('INVESTMENT_IMPACT_SELECTION');
    if (!state || state.length === 0) return '-';
    return state.map(ir => ir.name).join(', ');
  };

  getInvestmentCapex = computed(() => {
    return this.budgetAllocation.is_capex;
  });

  private isStepComplete(): boolean {
    return this.form.valid && !!this.wizardService.getState('BUDGET_ALLOCATION')?.price;
  }

  ngOnInit(): void {
    if (this.state()) this.form.setValue(this.state());
    const assetState = this.wizardService.getState('ASSET_SELECTION'),
      newAssetState = this.wizardService.getState('NEW_ASSET_STEP');
    this.form.valueChanges.subscribe(value => {
      if (this.isStepComplete()) {
        this.wizardService.unblockCurrentStep();
      } else {
        this.wizardService.blockCurrentStep(this.translate.instant('Please fill all required fields to continue'));
      }
      this.wizardService.setState('INVESTMENT_SUBMIT_STEP', <InvestmentSubmitForm>value);
    });

    const isComplementaryInvestment = !!this.wizardService.getState('LINKED_TO');

    if (!this.form.controls['label'].value && !isComplementaryInvestment) {
      if (this.investmentType.replacement && assetState) {
        this.form.controls['label'].setValue(this.translate.instant('Replace {equipment}', { equipment: assetState.label?.toLowerCase() }));
      } else if (this.investmentType.renew && assetState) {
        this.form.controls['label'].setValue(this.translate.instant('Renew {equipment}', { equipment: assetState.label?.toLowerCase() }));
      } else if (this.investmentType.maintenance && assetState) {
        this.form.controls['label'].setValue(this.translate.instant('Work on {equipment}', { equipment: assetState.label?.toLowerCase() }));
      } else if (this.investmentType.creation_of_equipment && newAssetState) {
        const equipment = newAssetState.value?.label?.toLowerCase() || '';
        this.form.controls['label'].setValue(this.translate.instant('New equipment for {assetType}', { assetType: equipment }));
      }
    }

    if (!this.isStepComplete()) {
      this.wizardService.blockCurrentStep();
    }
  }

  updateBudgetAllocation(field: keyof BudgetForm, value: any) {
    let budgetAllocation = this.wizardService.getState('BUDGET_ALLOCATION');
    budgetAllocation = { ...budgetAllocation, [field]: value };
    this.wizardService.setState('BUDGET_ALLOCATION', budgetAllocation);
  }

  getBudget() {
    const budgetAllocation = this.wizardService.getState('BUDGET_ALLOCATION');
    const price = budgetAllocation.price;
    if (!price) return '-';
    const priceFmt = this.i18n.formatPriceWithThousands(price),
      year = this.translate.instant('in {{year}}', { year: budgetAllocation.final_schedule_to }),
      origin = budgetAllocation.budget_origin?.name || '-';
    return `${priceFmt} ${year} (${origin})`;
  }

  getInvestmentStatus() {
    const budgetAllocation = this.wizardService.getState('BUDGET_ALLOCATION');
    return budgetAllocation.status ? budgetAllocation.status.name : '-';
  };

  openQuickEdit(step: WizardStep['key'] = null) {
    this.rightPanelDialogService.open(
      InvestmentWizardQuickEditComponent,
      {
        autoFocus: false,
        closeOnBackdropClick: true,
        context: {
          stepKey: step,
        },
      },
    ).onClose.subscribe(() => {
      if (this.isStepComplete()) {
        this.wizardService.unblockCurrentStep();
      } else {
        this.wizardService.blockCurrentStep(this.translate.instant('Please fill all required fields to continue'));
      }
    })
  }

  getPerimeter() {
    const site = this.wizardService.getState('SITE_SELECTION');
    return site.name;
  }

  getInvestmentTaxonomy() {
    // return this.budgetAllocation.aligned_taxonomy ? this.translate.instant('Yes') : this.translate.instant('No');
    return '-';
  }

  getTechnicalNomenclature() {
    const selectedAsset = this.wizardService.getState('ASSET_SELECTION'),
      technicalNom = this.wizardService.getState('NOMENCLATURE_TECHNIQUE_SELECTION'),
      newAssetState = this.wizardService.getState('NEW_ASSET_STEP'),
      ivType = this.wizardService.getState('INVESTMENT_TYPE_SELECTION');
    // nomenclature for prev asset
    if ((ivType.maintenance || ivType.renew) && selectedAsset) {
      const assetTypeId = selectedAsset.asset_type;
      return this.assetTypes().find(at => at.id === assetTypeId)?.name;
    }
    if (ivType.replacement) {
      const assetTypeId = selectedAsset.asset_type;
      return this.assetTypes().find(at => at.id === assetTypeId)?.name;
    }
    // nomenclature for creation new asset
    if (ivType.creation_of_equipment && newAssetState) {
      const assetType = newAssetState.value.asset_type;
      return assetType?.name;
    }
    // nomenclature for global investment
    if (technicalNom && ivType.maintenance) {
      if (technicalNom.sub_category) {
        return `${technicalNom.super_category?.name} > ${technicalNom.category?.name} > ${technicalNom.sub_category?.name}`;
      }
      return technicalNom.super_category.name;
    }
    return '-';
  }

  getFurtherInformation() {
    return this.wizardService.getState('INVESTMENT_FURTHER_INFORMATION_SELECTION')?.name || '-';
  }

  getGainEnergyCarbon() {
    const action = this.wizardService.getState('GAIN_ENERGY_CARBON');
    if (!action || !action.initiative_type) return '-';
    if (action.initiative_type.gain_type === 'QUIT_FOSSIL') return this.translate.instant('Abandon energy fossil');
    const gains = action.gains as KPI.InitiativeGain[];
    if (action.initiative_type.gain_type === 'None') return this.translate.instant('No gains to be made on this type of action');
    const firstGain = gains[0];
    if (!firstGain) return '-';
    if (action.initiative_type.gain_type === 'ENERGY') {
      return this.translate.instant('{savings} kWh (expected gain)',
        { savings: formatNumber(firstGain.theoretical_savings_full_year, this.i18n.getCulture()) },
      );
    } else if (action.initiative_type.gain_type === 'CARBON') {
      return this.translate.instant('{savings} kgCO2 (expected gain)',
        { savings: formatNumber(firstGain.theoretical_greenhouse_gas_savings_full_year, this.i18n.getCulture()) },
      );
    }
    return '-';
  }

  getInvestmentReasons() {
    return this.wizardService.getState('INVESTMENT_REASON_SELECTION');
  }

  getInvestmentCategory() {
    const category = this.wizardService.getState('INVESTMENT_CATEGORY_SELECTION');
    return category ? category.name : '-';
  }

  getInvestmentPriority() {
    return this.budgetAllocation.priority;
  }

  getLinkedEquipmentDisplay() {
    const asset = this.wizardService.getState('ASSET_SELECTION');
    return asset?.label ?? '-';
  }

  getInvestmentNotation() {
    const notation = this.wizardService.getState('INVESTMENT_NOTATION_STEP');
    if (!notation || !notation.enabled) return this.translate.instant('No impact');
    return this.ivNotationService.labelFromState(notation.note);
  }
}
